import { getEase, Tween } from "@westpierstudio/luma/createjs";
import { View } from "@westpierstudio/luma/mvc";
import { ElasticCache, ExternalItem } from "@westpierstudio/luma/utils/elastic-cache";
import { GameServices } from "game/GameServices";
import { Reel } from "./Reel";
import { ReelFrames } from "./ReelFrames";
import { BonusSymbolPositions, JumpingWildsSet, OverlaySymbolState, ReelsLayout, SpinConfig } from "./ReelsModel";
import { ReelStrips } from "servertypes/gameevents/OpenGame";
import { OverlayID, OverlaySymbols, ReelSetOverlays, ReelSetSymbols, SymbolID, SymbolPos } from "servertypes/SharedTypes";
import { cloneDeep } from "lodash";
import { Signal } from "type-signals";
import { Spine } from "pixi-spine";
import { drawRect, clearSpineSlot } from "utils/Misc";
import { Emitter } from "pixi-particles";
import { Container, Sprite } from "pixi.js";
import { minimumHeightSpecSelector, orientationSpecSelector } from "utils/Responsive";

const POT_COIN_REELS = [0,1,2,3,4,5,6,7];

class ReelsSignals {
    reelStop = new Signal<(reelIndex: number) => void>();
    reelStopSound = new Signal();
    coinHit1Sound = new Signal();
    coinHit2Sound = new Signal();
    coinHit3Sound = new Signal();
    jumpingWildSound = new Signal();
    jumpingWildHitSound = new Signal<(hitNum: number, multiplier: number) => void>();
    reelStopPotCoin = new Signal<(reelIndex: number) => void>();
    spinComplete = new Signal();
    scatterWinComplete = new Signal();
    jumpingWildsSetComplete = new Signal();
    sparkleRequest = new Signal<(reelIndex: number, distance: number, reelSpeed?: number | undefined) => void>();
    dragonRoar = new Signal<(inAnimLoop: boolean) => void>();
    pandaRoar = new Signal<(inAnimLoop: boolean) => void>();
}

class WildContainer {
    wild!: Sprite;
    particles!: Emitter;

    setup(wild: Sprite, particles: Emitter): void {
        this.wild = wild;
        this.particles = particles;
    }

    activate(): void {
        this.wild.visible = true;
        this.particles.emit = true;
    }

    deactivate(): void {
        this.wild.visible = false;
        this.particles.emit = false;
        this.particles.cleanup();
    }

    setPos(x: number, y: number): void {
        this.wild.x = x;
        this.wild.y = y;

        this.particles.spawnPos.x = x;
        this.particles.spawnPos.y = y;
    }
}

export type SymbolCache = {[key: string]: ElasticCache<Spine & Container>};

export class Reels extends View {
    private maskedContainer!: Container;
    private dragonsReelsContainer!: Container;
    private pandasReelsContainer!: Container;
    private symbolCache: SymbolCache;
    private overlayCache: ElasticCache<Spine & Container>;
    private overlayFrameCache: ElasticCache<Spine & Container>;
    private dragonsReels: Reel[] = [];
    private pandasReels: Reel[] = [];
    private allReels: Reel[] = [];
    private reelFrames!: ReelFrames;
    private movingWildsContainer!: Container;
    private overlaysContainer!: Container;
    private movingWildsCache!: ElasticCache<WildContainer>;
    private movingWildsResetMap = new Map<number, ExternalItem<WildContainer>>;
    private overlaysResetMap = new Map<number, ExternalItem<Spine & Container>>;
    private overlaysFrameResetMap = new Map<number, ExternalItem<Spine & Container>>;
    private spinConfig!: SpinConfig;
    private lastSpinningReelID = 0;
    private scatterWinsComplete = 0;
    private coinHitCount = 0;
    private jumpingWildHitsList: number[] = [];
    private jumpingWildHits: number = 0;
    private playerBet: number = 88;
    private layout! : ReelsLayout;
    signals = new ReelsSignals();

    constructor(protected override services: GameServices) {
        super(services);

        this.symbolCache = this.setupSymbolCache();
        this.overlayCache = this.setupOverlayCache();
        this.overlayFrameCache = this.setupOverlayFrameCache();

        services.layout.onResize.add(() => this.resize());
    }

    setup(layout: ReelsLayout): void {

        const {factory, resources} = this.services;

        const spinByOverlayWeights = resources.getSetting(`overlays`).spinByWeightsPerReel;

        this.services.factory.applySpec(this, {x: layout.x, y: layout.y});

        this.setupBackgroundContainer(layout.dragonsBackground);
        this.setupBackgroundContainer(layout.pandasBackground);

        this.reelFrames = new ReelFrames(this.services, layout);
        this.addChild(this.reelFrames);

        this.maskedContainer = factory.createContainer({x: 0, y: 0}, this);

        this.dragonsReelsContainer = factory.createContainer(layout.dragonsReels, this.maskedContainer);
        this.pandasReelsContainer = factory.createContainer(layout.pandasReels, this.maskedContainer);

        this.setupReelSet(layout, this.dragonsReels, this.dragonsReelsContainer, spinByOverlayWeights.dragons, `dragons`);
        this.setupReelSet(layout, this.pandasReels, this.pandasReelsContainer,spinByOverlayWeights.pandas, `pandas`);

        this.setupMask(layout);

        this.setupMovingWilds();
        this.overlaysContainer = this.services.factory.createContainer({}, this);

        this.layout = layout;
        this.resize();
    }

    setupNeonDividers(): void {
        this.reelFrames.setupNeonDividers();
    }

    setupNormalDividers(): void {
        this.reelFrames.setupNormalDividers();
    }

    setupBaseFlames(): void {
        this.reelFrames.setupBaseFlames();
    }

    setupVSDragonFlames(): void {
        this.reelFrames.setupVSDragonFlames();
    }

    setupVSPandaFlames(): void {
        this.reelFrames.setupVSPandaFlames();
    }

    setupFreeGamesFrames(): void {
        this.reelFrames.setupFreeGamesFrames();
    }

    setReelStrips(dragonsReelStrips: ReelStrips, pandasReelStrips: ReelStrips): void {

        for (const [reelIndex, value] of dragonsReelStrips.entries()) {
            this.dragonsReels[reelIndex].setReelStrip(value);
        }

        for (const [reelIndex, value] of pandasReelStrips.entries()) {
            this.pandasReels[reelIndex].setReelStrip(value);
        }
    }

    setReelSymbols(dragonsSymbols: ReelSetSymbols, pandasSymbols: ReelSetSymbols, overlays: OverlaySymbolState): void {

        for (const [reelIndex, value] of dragonsSymbols.entries()) {
            this.dragonsReels[reelIndex].setSymbols(value, overlays.dragons[reelIndex]);
        }

        for (const [reelIndex, value] of pandasSymbols.entries()) {
            this.pandasReels[reelIndex].setSymbols(value, overlays.pandas[reelIndex]);
        }
    }

    setSpinConfig(spinConfig: SpinConfig): void {
        this.spinConfig = spinConfig;

        for (const reel of this.allReels) {
            reel.setSpinConfig(spinConfig);
        }
    }

    spinAllReels(dragonsResult: ReelSetSymbols, pandasResult: ReelSetSymbols, overlayResult: OverlaySymbolState, playerBet?: number): void {

        if (playerBet) {

            this.playerBet = playerBet;
        }

        this.coinHitCount = 0;
        this.lastSpinningReelID = 7;
        this.spinReelSet(this.dragonsReels, dragonsResult, overlayResult.dragons);
        this.spinReelSet(this.pandasReels, pandasResult, overlayResult.pandas);
    }

    spinDragonsReels(spinResult: ReelSetSymbols, overlayResult: ReelSetOverlays): void {

        this.coinHitCount = 0;
        this.lastSpinningReelID = 3;
        this.spinReelSet(this.dragonsReels, spinResult, overlayResult);
    }

    spinPandasReels(spinResult: ReelSetSymbols, overlayResult: ReelSetOverlays): void {

        this.coinHitCount = 0;
        this.lastSpinningReelID = 7;
        this.spinReelSet(this.pandasReels, spinResult, overlayResult);
    }

    slamStopAllReels(dragonsResult: ReelSetSymbols, pandasResult: ReelSetSymbols, overlayResult: OverlaySymbolState): void {

       this.slamStopDragonsReels(dragonsResult, overlayResult.dragons);
       this.slamStopPandasReels(pandasResult, overlayResult.pandas);
    }

    slamStopDragonsReels(spinResult: ReelSetSymbols, overlayResult: ReelSetOverlays): void {

        for (const [i, reel] of this.dragonsReels.entries()) {
            reel.slamStop(spinResult[i], overlayResult[i]);
        }
    }

    slamStopPandasReels(spinResult: ReelSetSymbols, overlayResult: ReelSetOverlays): void {

        for (const [i, reel] of this.pandasReels.entries()) {
            reel.slamStop(spinResult[i], overlayResult[i]);
        }
    }

    /*
        Jumping wilds pass under the overlays.
        Hide the on reel overlays + add instances to the overlaid overlaysContainer
    */
    setAboveReelsOverlayMode(overlayResult: OverlaySymbolState, layout: ReelsLayout): void {

        const {reelsPerSet} = layout;

        for (const [i, reel] of overlayResult.dragons.entries()) {
            for (const [j, id] of reel.entries()) {

                if (id !== `E`) {
                    this.setAboveReelsOverlay(id, i, j, layout);
                }
            }
        }

        for (const [i, reel] of overlayResult.pandas.entries()) {
            for (const [j, id] of reel.entries()) {

                if (id !== `E`) {
                    this.setAboveReelsOverlay(id, i+reelsPerSet, j, layout);
                }
            }
        }
    }

    setOnReelsOverlayMode(): void {

        this.overlaysContainer.removeChildren();

        for (const reel of this.allReels) {
            reel.showOverlays();
        }
    }

    resetJumpingWildHitData(): void {
        this.jumpingWildHitsList = [];
        this.jumpingWildHits = 0;
    }

    showJumpingWildsSet(jumpingWilds: JumpingWildsSet, layout: ReelsLayout, restoreMode: boolean = false): void{

        const settings = this.services.resources.getSetting(`jumpingWilds`);
        const baseMoveTime: number = settings.baseMoveTime;
        const targetReelMoveTimes: number[] = settings.targetReelMoveTimes;

        let moveTime;
        let counter = 0;

        for (const item of jumpingWilds) {

            const targetRow = item.target.row;
            const targetCol = item.target.column;

            const wild = this.movingWildsCache.get();
            wild.reference.setPos(layout.globalSymbolPositions[item.source.column][item.source.row].x - layout.x, layout.globalSymbolPositions[item.source.column][item.source.row].y - layout.y);
            this.movingWildsResetMap.set(wild.id, wild);

            if (restoreMode) {

                moveTime = 0;
            } else {

                moveTime = baseMoveTime + targetReelMoveTimes[targetCol];
                this.signals.jumpingWildSound.dispatch();
            }

            Tween.get(wild.reference.particles.spawnPos)
            .to({
                x: layout.globalSymbolPositions[targetCol][targetRow].x - layout.x,
                y: layout.globalSymbolPositions[targetCol][targetRow].y - layout.y
            },
            moveTime,
            getEase(settings.moveEase));

            Tween.get(wild.reference.wild)
            .to({
                x: layout.globalSymbolPositions[targetCol][targetRow].x - layout.x,
                y: layout.globalSymbolPositions[targetCol][targetRow].y - layout.y
            },
            moveTime,
            getEase(settings.moveEase))
            .call(() => {
                this.movingWildsResetMap.delete(wild.id);
                this.movingWildsCache.free(wild);
                this.allReels[targetCol].setSymbolToAlteredWild(targetRow+1, item.multiplier);

                counter++;

                if (counter === jumpingWilds.length) {
                    this.signals.jumpingWildsSetComplete.dispatch();
                }

                if (this.jumpingWildHitsList.find(element => element == targetCol) === undefined) {

                    this.jumpingWildHitsList.push(targetCol);
                    this.jumpingWildHits += 1;

                    if (!restoreMode) {

                        this.signals.jumpingWildHitSound.dispatch(this.jumpingWildHits, item.multiplier);
                    }
                }
            });
        }
    }

    startWinAnims(animateState: boolean[][]): void {

        for (const [i, reel] of this.allReels.entries()) {
            reel.startWinAnimations(animateState[i]);
        }
    }

    getWinSymbols(winState: boolean[][]): string[] {

        let winners: string[] = [];

        for (const [i, reel] of this.allReels.entries()) {
            winners = winners.concat(reel.getWinSymbols(winState[i]));
        }

        return winners;
    }

    bonusWinAnimations(symbols: BonusSymbolPositions): void {

        this.scatterWinsComplete = 0;

        for (const value of symbols.dragons) {
            this.dragonsReels[value.column].bonusWinAnimations(value.row);
        }

        for (const value of symbols.pandas) {
            this.pandasReels[value.column].bonusWinAnimations(value.row);
        }
    }

    startScatterWinAnimationSequences(symbols: BonusSymbolPositions): void {

        this.scatterWinsComplete = 0;

        for (const value of symbols.dragons) {
            this.dragonsReels[value.column].startScatterWinAnimationSequence(value.row);
        }

        for (const value of symbols.pandas) {
            this.pandasReels[value.column].startScatterWinAnimationSequence(value.row);
        }
    }

    startScatterWinAnimationLoops(symbols: BonusSymbolPositions, complete: boolean = true): void {

        this.scatterWinsComplete = 0;

        for (const value of symbols.dragons) {
            this.dragonsReels[value.column].startScatterWinAnimationLoop(value.row, complete);
        }

        for (const value of symbols.pandas) {
            this.pandasReels[value.column].startScatterWinAnimationLoop(value.row, complete);
        }
    }

    stopWinAnims(): void {

        for (const reel of this.allReels) {
            reel.stopWinAnimations();
        }
    }

    override reset(): void {

        for (const reel of this.allReels) {
            reel.reset();
        }

        this.lastSpinningReelID = 0;
        this.scatterWinsComplete = 0;

        for (const item of this.movingWildsResetMap.values()) {
            this.movingWildsCache.free(item);
        }
        this.movingWildsResetMap.clear();

        for (const item of this.overlaysResetMap.values()) {
             this.overlayCache.free(item);
        }
        this.overlaysResetMap.clear();

        for (const item of this.overlaysFrameResetMap.values()) {
            this.overlayFrameCache.free(item);
       }
       this.overlaysFrameResetMap.clear();

        this.setOnReelsOverlayMode();
    }

    private setupSymbolCache(): SymbolCache {

        const {factory, resources} = this.services;

        const symbolCache: SymbolCache = {};

        for (const [key, spec] of Object.entries(resources.getSpec(`symbols`).spines)) {
            symbolCache[key] = new ElasticCache<Spine & Container>(
                () => {
                    return factory.createSpine(spec as any);
                },
                () => {

                },
                (item) => {
                    item.x = 0;
                    item.y = 0;
                    item.state.clearTracks();
                    item.state.clearListeners();
                }
            );
        }

        return symbolCache;
    }

    private setupOverlayCache(): ElasticCache<Spine & Container> {

        const {factory, resources} = this.services;
        const spec = resources.getSpec(`symbols`);

        return new ElasticCache<Spine & Container>(
            () => {

                return factory.createSpine(spec.spines.O, this);
            },
            (item) => {

                if (this.playerBet < 88) {

                    item.tint = 0x909090;
                }

                item.visible = true;
            },
            (item) => {

                item.tint = 0xffffff;
                item.visible = false;
                item.x = 0;
                item.y = 0;
                item.state.clearTracks();
                item.state.clearListeners();
                clearSpineSlot(item, `dynamic-slot`);
            }
        );
    }

    private setupOverlayFrameCache(): ElasticCache<Spine & Container> {

        const {factory, resources} = this.services;
        const spec = resources.getSpec(`symbols`);

        return new ElasticCache<Spine & Container>(
            () => {

                return factory.createSpine(spec.spines.OverlayFrame, this);
            },
            (item) => {

                if (this.playerBet < 88) {

                    item.tint = 0x909090;
                }

                item.visible = true;
            },
            (item) => {

                item.tint = 0xffffff;
                item.visible = false;
                item.x = 0;
                item.y = 0;
                item.state.clearTracks();
                item.state.clearListeners();
            }
        );
    }

    private setupBackgroundContainer(spec: Record<string, any>): Container {

        const {factory} = this.services;

        const container = factory.createContainer(spec, this);

        for (const childName of Object.keys(spec.slices)) {
            factory.createSprite(spec.slices[childName], container);
        }

        return container;
    }

    private setupReelSet(layout: ReelsLayout, reelSet: Reel[], container: Container, overlayWeights: number[], team: string): void {

        const {reelsPerSet, setPositions, symbolSize} = layout;

        let reel;

        for (let i=0; i<reelsPerSet; i++) {

            let reelIndex = i;
            if (team === `pandas`) {
                reelIndex += 4;
            }

            reel = new Reel(
                this.services,
                this.allReels.length,
                symbolSize.height,
                this.symbolCache,
                this.overlayCache,
                this.overlaysResetMap,
                this.overlayFrameCache,
                this.overlaysFrameResetMap,
                overlayWeights[i],
                reelIndex
            );

            reel.x = setPositions[i].x;

            reel.signals.reelStopSound.add(() => this.signals.reelStopSound.dispatch());
            reel.signals.dragonRoar.add((inAnim: boolean) => { this.signals.dragonRoar.dispatch(inAnim); }, this);
            reel.signals.pandaRoar.add((inAnim: boolean) => { this.signals.pandaRoar.dispatch(inAnim); }, this);
            reel.signals.reelStop.add(this.reelStopped, this);
            reel.signals.scatterWinComplete.add(() => {
                if (++this.scatterWinsComplete === 3) {
                    this.signals.scatterWinComplete.dispatch();
                }
            });
            reel.signals.sparkleRequest.add(this.sparkleRequest, this);

            reel.signals.coinHit.add(() => this.doCoinHit(reelIndex));

            container.addChild(reel);

            reelSet.push(reel);
            this.allReels.push(reel);
        }
    }

    private setupMovingWilds(): void {

        this.movingWildsContainer = this.services.factory.createContainer({}, this);

        const {factory, resources} = this.services;

        this.movingWildsCache = new ElasticCache<WildContainer>(
            () => {
                const wildContainer = new WildContainer();
                const particles = factory.createParticleEmitter(resources.getSpec(`particles`).jumpingwild, this.movingWildsContainer);
                const wild = factory.createSprite(resources.getSpec(`symbols`).WW, this.movingWildsContainer);
                wildContainer.setup(wild, particles);

                return wildContainer;
            },
            (wildContainer) => {
                wildContainer.activate();
            },
            (wildContainer) => {
                wildContainer.deactivate();
            }
        );
    }

    private setupMask(layout: ReelsLayout): void {

        const {x, y, width, height} = layout.mask;

        const graphic = drawRect(width, height, 0xFFFFFF);
        graphic.x = x;
        graphic.y = y;

        this.maskedContainer.addChild(graphic);
        this.maskedContainer.mask = graphic;
    }

    //generate a random stop position for the reel + swap the symbols on the map so they land on the result
    private configureReelForSpin(reel: Reel, symbols: SymbolID[], overlays: OverlayID[]): number {

        const rnd_stop = reel.getRandomReelStripIndex();

        /*
            provided result is reverse to reel strip order i.e:
                stepping through a reel strip 7,3,6,4,1, with a stop index of 2 we'd expect
                7,3,6 to be on the reels, with 6 at the top as it was the last symbol.
                Instead we need to display 6,3,7, so reverse the provided result before subbing.
        */
        reel.swapReelStripSymbols(rnd_stop, cloneDeep(symbols).reverse());
        reel.setOverlayResult(cloneDeep(overlays).reverse() as OverlaySymbols);

        return rnd_stop;
    }

    private spinReelSet(reels: Reel[], spinResult: ReelSetSymbols, overlayResult: ReelSetOverlays): void {

        let rnd_stop;

        for (const [i, reel] of reels.entries()) {

            rnd_stop = this.configureReelForSpin(reel, spinResult[i], overlayResult[i]);
            reel.spin(rnd_stop);
        }
    }

    private reelStopped(id: number): void {

        this.signals.reelStop.dispatch(id);

        if (POT_COIN_REELS.includes(id)) {
            this.signals.reelStopPotCoin.dispatch(id);
        }

        if (id === this.lastSpinningReelID) {
            this.signals.spinComplete.dispatch();
        }
    }

    private sparkleRequest(reelIndex: number, distance: number, reelSpeed?: number | undefined): void {
        this.signals.sparkleRequest.dispatch(reelIndex, distance, reelSpeed);
    }

    private setAboveReelsOverlay(id: OverlayID, reelIndex: number, symbolIndex: number, layout: ReelsLayout): void {

        const {reelsPerSet} = layout;

        let symbol;
        if (reelIndex < reelsPerSet) {

            symbol = this.dragonsReels[reelIndex].getSymbol(symbolIndex+1);
        } else {

            symbol = this.pandasReels[reelIndex-reelsPerSet].getSymbol(symbolIndex+1);
        }

        const overlay = symbol.getOverlay();
        if (overlay)
        {

            this.overlaysContainer.addChildAt(overlay.reference, 0);

            overlay.reference.x = layout.globalSymbolPositions[reelIndex][symbolIndex].x - layout.x;
            overlay.reference.y = layout.globalSymbolPositions[reelIndex][symbolIndex].y - layout.y;
        }
    }

    private doCoinHit(reelIndex: number): void {

        if (reelIndex - this.coinHitCount > 5) {

            return;
        }

        switch (this.coinHitCount) {
            case 0:
                this.signals.coinHit1Sound.dispatch();
                break;
            case 1:
                this.signals.coinHit2Sound.dispatch();
                break;
            case 2:
                this.signals.coinHit3Sound.dispatch();
                break;
        }

        this.coinHitCount++;
    }

    getSymbolGlobalPosition(reelIndex: number, symbolIndex: number, layout: ReelsLayout) {
        const {reelsPerSet} = layout;
        const reels = (reelIndex < reelsPerSet) ? this.dragonsReels : this.pandasReels;
        reelIndex = reelIndex % reelsPerSet;
        return reels[reelIndex].getVisibleSymbolGlobalPosition(symbolIndex);
    }

    resize() {
        const {factory} = this.services;
        const portraitSpec = minimumHeightSpecSelector(this.services, this.layout.portrait);
        factory.applySpec(this, orientationSpecSelector(this.services, this.layout, portraitSpec));
    }
}